/**
 * Drop story gallery.
 *
 * Presentation only — this file deliberately contains no cart or checkout
 * logic. Add to cart is handled by Horizon's <product-form-component>
 * (assets/product-form.js), which posts to Theme.routes.cart_add_url so that
 * Shopify's standard `product_added_to_cart` pixel event still fires. Do not
 * reimplement add-to-cart here.
 */

/**
 * Every component below registers its listeners against an AbortController and
 * aborts on disconnect. Horizon re-renders parts of the DOM when a variant is
 * chosen, which runs connectedCallback again — without this, listeners stack up
 * and a toggle fires twice per click, so it never appears to change.
 */
class DropGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-drop-track]');
    if (!this.track) return;

    this.count = this.track.querySelectorAll('[data-drop-slide]').length;

    // A coming-soon chapter's slides are nested blocks, so Liquid never learns
    // how many there are, nor which is which. Both the dots and the zoom
    // buttons' indices are derived here, where the slides are all in the DOM,
    // and before anything below is bound to them.
    this.buildAutoDots();
    this.indexZoomButtons();

    this.dots = Array.from(this.querySelectorAll('[data-drop-dot]'));

    if (this.count < 2) {
      this.querySelectorAll('[data-drop-dots], [data-drop-nav]').forEach((el) => el.remove());
      return;
    }

    this.prev = this.querySelector('[data-drop-nav="prev"]');
    this.next = this.querySelector('[data-drop-nav="next"]');

    this.onScroll = this.sync.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    window.addEventListener('resize', this.onScroll, { signal });

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i), { signal }));

    this.querySelectorAll('[data-drop-zoom]').forEach((button) => {
      button.addEventListener('click', () => this.openLightbox(Number(button.dataset.dropZoom)), { signal });
    });
    this.prev?.addEventListener('click', () => this.goTo(this.index() - 1), { signal });
    this.next?.addEventListener('click', () => this.goTo(this.index() + 1), { signal });

    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    // Rebuilt on next open; leaving it would orphan a dialog on <body>.
    this.lightbox?.remove();
    this.lightbox = null;
  }

  /**
   * Fills a [data-drop-dots-auto] container with one dot per slide. Idempotent,
   * because the theme editor reconnects the element whenever a slide is added,
   * removed or reordered.
   */
  buildAutoDots() {
    const container = this.querySelector('[data-drop-dots-auto]');
    if (!container || container.childElementCount === this.count) return;

    container.replaceChildren();

    for (let i = 0; i < this.count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'drop-gallery__dot';
      dot.setAttribute('data-drop-dot', '');
      dot.setAttribute('aria-label', `Photo ${i + 1} of ${this.count}`);
      container.appendChild(dot);
    }
  }

  /**
   * Numbers the zoom buttons of a gallery whose slides are nested blocks, so
   * the full-screen viewer opens on the photo that was tapped. Opt-in, so the
   * normal chapter keeps the indices Liquid already gave it.
   */
  indexZoomButtons() {
    if (!this.hasAttribute('data-drop-index-auto')) return;

    const buttons = this.querySelectorAll('[data-drop-zoom]');

    buttons.forEach((button, i) => {
      button.setAttribute('data-drop-zoom', String(i));
      button.setAttribute('aria-label', `Open photo ${i + 1} of ${buttons.length} full screen`);
    });
  }

  index() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  goTo(i) {
    const target = Math.max(0, Math.min(this.count - 1, i));
    this.track.scrollTo({ left: this.track.clientWidth * target, behavior: 'smooth' });
  }

  /**
   * Full-screen viewer. Images are cloned from the gallery rather than
   * re-rendered, so the browser reuses what it already downloaded; only the
   * `sizes` hint changes, because full screen is wider than the gallery.
   */
  openLightbox(index) {
    if (!this.lightbox) this.buildLightbox();

    this.lightbox.showModal();

    // Label it from the known index rather than waiting on layout, so it is
    // correct even before the track has been scrolled into position.
    this.lightboxCount.textContent = `${index + 1} / ${this.count}`;

    requestAnimationFrame(() => {
      this.lightboxTrack.scrollLeft = this.lightboxTrack.clientWidth * index;
    });
  }

  buildLightbox() {
    const dialog = document.createElement('dialog');
    dialog.className = 'drop-lightbox';

    const track = document.createElement('div');
    track.className = 'drop-lightbox__track';

    this.querySelectorAll('.drop-gallery__image').forEach((image) => {
      const slide = document.createElement('div');
      slide.className = 'drop-lightbox__slide';

      const clone = image.cloneNode(true);
      clone.removeAttribute('class');
      clone.setAttribute('sizes', '100vw');
      clone.setAttribute('loading', 'lazy');

      const zoom = document.createElement('drop-zoom');
      zoom.appendChild(clone);
      slide.appendChild(zoom);
      track.appendChild(slide);
    });

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'drop-lightbox__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    close.addEventListener('click', () => dialog.close());

    // Leaving a photo zoomed in would mean reopening to a random crop.
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('drop-zoom').forEach((zoom) => zoom.reset());
    });

    const count = document.createElement('p');
    count.className = 'drop-lightbox__count';

    // Explicit controls: drag-zoom-wrapper consumes touchmove for pinch/pan, so
    // swiping cannot be relied on, and a mouse wheel scrolls the page axis, not
    // this one. Buttons and arrow keys work on every input type.
    const nav = (cls, label, path, delta) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `drop-lightbox__nav drop-lightbox__nav--${cls}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;
      b.addEventListener('click', () => this.lightboxGoTo(this.lightboxIndex() + delta));
      return b;
    };
    const prev = nav('prev', 'Previous photo', 'M15 18l-6-6 6-6', -1);
    const next = nav('next', 'Next photo', 'M9 18l6-6-6-6', 1);

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.lightboxGoTo(this.lightboxIndex() - 1);
      if (event.key === 'ArrowRight') this.lightboxGoTo(this.lightboxIndex() + 1);
    });

    // Clicking the backdrop (outside any slide) closes, as a modal should.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.classList.contains('drop-lightbox__slide')) dialog.close();
    });
    track.addEventListener('scroll', () => this.syncCount(), { passive: true });

    dialog.append(track, close, count, prev, next);
    document.body.appendChild(dialog);

    this.lightbox = dialog;
    this.lightboxTrack = track;
    this.lightboxCount = count;
  }

  lightboxIndex() {
    return Math.round(this.lightboxTrack.scrollLeft / this.lightboxTrack.clientWidth);
  }

  lightboxGoTo(i) {
    const target = Math.max(0, Math.min(this.count - 1, i));
    this.lightboxTrack.scrollTo({ left: this.lightboxTrack.clientWidth * target, behavior: 'smooth' });
  }

  syncCount() {
    if (!this.lightboxCount) return;
    const i = Math.round(this.lightboxTrack.scrollLeft / this.lightboxTrack.clientWidth);
    this.lightboxCount.textContent = `${i + 1} / ${this.count}`;
  }

  sync() {
    const i = this.index();
    this.dots.forEach((dot, j) => {
      dot.classList.toggle('is-active', j === i);
      dot.setAttribute('aria-current', j === i ? 'true' : 'false');
    });
    if (this.prev) this.prev.disabled = i <= 0;
    if (this.next) this.next.disabled = i >= this.count - 1;
  }
}

const ZOOM_MAX = 4;
const ZOOM_DOUBLE_TAP = 2.5;
const DOUBLE_TAP_MS = 300;
// A second tap only counts if it lands near the first, and a finger that
// travelled is a swipe, not a tap. Without both checks two quick swipes
// register as a double-tap and zoom unexpectedly.
const DOUBLE_TAP_DISTANCE = 40;
const TAP_MOVE_TOLERANCE = 10;

/**
 * Pinch/double-tap zoom for the full-screen viewer.
 *
 * Horizon's drag-zoom-wrapper calls preventDefault on every touchstart and
 * touchmove before inspecting them, so it swallowed the one-finger swipe that
 * moves between photos. This claims a gesture only when it genuinely needs it:
 * two fingers (pinch), or one finger while already zoomed in (pan). At scale 1
 * a single finger is left alone, so the swipe belongs to the gallery track.
 */
class DropZoom extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('img');
    if (!this.image) return;

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.reset();
    this.addEventListener('touchstart', this.onTouchStart, { passive: false, signal });
    this.addEventListener('touchmove', this.onTouchMove, { passive: false, signal });
    this.addEventListener('touchend', this.onTouchEnd, { signal });
    this.addEventListener('dblclick', this.onDoubleClick, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  reset() {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.apply();
  }

  apply() {
    this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.classList.toggle('is-zoomed', this.scale > 1);
  }

  clampOffset() {
    const rect = this.getBoundingClientRect();
    const maxX = Math.max(0, (rect.width * (this.scale - 1)) / 2);
    const maxY = Math.max(0, (rect.height * (this.scale - 1)) / 2);
    this.x = Math.min(maxX, Math.max(-maxX, this.x));
    this.y = Math.min(maxY, Math.max(-maxY, this.y));
  }

  static distance(touches) {
    return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY) || 1;
  }

  onTouchStart = (event) => {
    if (event.touches.length === 2) {
      event.preventDefault();
      this.pinchFrom = DropZoom.distance(event.touches);
      this.scaleFrom = this.scale;
      return;
    }
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const point = { x: touch.clientX, y: touch.clientY };

    if (
      this.lastTap &&
      event.timeStamp - this.lastTap.time < DOUBLE_TAP_MS &&
      Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < DOUBLE_TAP_DISTANCE
    ) {
      event.preventDefault();
      this.lastTap = null;
      this.toggleZoom();
      return;
    }
    this.lastTap = { time: event.timeStamp, x: point.x, y: point.y };
    this.touchStart = point;

    if (this.scale > 1) {
      event.preventDefault();
      this.panFrom = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        offsetX: this.x,
        offsetY: this.y,
      };
    }
  };

  onTouchMove = (event) => {
    if (event.touches.length === 2 && this.pinchFrom) {
      event.preventDefault();
      const ratio = DropZoom.distance(event.touches) / this.pinchFrom;
      this.scale = Math.min(ZOOM_MAX, Math.max(1, this.scaleFrom * ratio));
      this.clampOffset();
      this.apply();
      return;
    }

    if (event.touches.length !== 1) return;

    // A finger that has travelled is a swipe; do not let it end as a tap.
    if (this.touchStart) {
      const moved = Math.hypot(
        event.touches[0].clientX - this.touchStart.x,
        event.touches[0].clientY - this.touchStart.y
      );
      if (moved > TAP_MOVE_TOLERANCE) this.lastTap = null;
    }

    if (this.panFrom) {
      event.preventDefault();
      this.x = this.panFrom.offsetX + (event.touches[0].clientX - this.panFrom.x);
      this.y = this.panFrom.offsetY + (event.touches[0].clientY - this.panFrom.y);
      this.clampOffset();
      this.apply();
    }
  };

  onTouchEnd = () => {
    this.pinchFrom = null;
    this.panFrom = null;
    this.touchStart = null;
    if (this.scale <= 1) this.reset();
  };

  onDoubleClick = (event) => {
    event.preventDefault();
    this.toggleZoom();
  };

  toggleZoom() {
    this.scale = this.scale > 1 ? 1 : ZOOM_DOUBLE_TAP;
    this.x = 0;
    this.y = 0;
    this.apply();
  }
}

// Kept in step with the drop-sheet-out animation in drop-story.css.
const SHEET_CLOSE_MS = 280;

// Matches the split-layout breakpoint in drop-story.css. In that layout the
// panel is already a full-height column showing everything, so there is no
// sheet: reserving its height as padding would leave the chapter no room at all.
const SPLIT_LAYOUT = '(min-width: 1000px), (orientation: landscape) and (max-height: 460px) and (min-width: 560px)';

/**
 * Details sheet.
 *
 * Opening lifts the buy panel into a near-full-height sheet over the chapter,
 * scrollable, dismissed by the close button, the scrim, or Escape.
 *
 * The panel is restyled in place rather than moved into a <dialog>: relocating
 * it would disconnect and reconnect the product form and variant picker inside
 * it, and nothing is worth risking the add-to-cart path. The cost is that this
 * is not a real modal, so background content stays reachable by screen readers.
 */
class DropPanel extends HTMLElement {
  connectedCallback() {
    this.handle = this.querySelector('[data-drop-handle]');
    this.closeButton = this.querySelector('[data-drop-close]');
    this.chapter = this.closest('.drop-chapter');
    this.scrim = this.chapter?.querySelector('[data-drop-scrim]');
    if (!this.handle) return;

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.splitLayout = window.matchMedia(SPLIT_LAYOUT);

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.open) this.setOpen(false);
    };

    // Collapse if the viewport grows into the split layout while open.
    this.onLayoutChange = () => {
      if (this.splitLayout.matches && this.open) this.setOpen(false);
    };
    this.splitLayout.addEventListener('change', this.onLayoutChange, { signal });

    this.handle.addEventListener('click', () => this.setOpen(true), { signal });

    // Controls that open the sheet scrolled to a particular part of it.
    this.querySelectorAll('[data-drop-open-details]').forEach((control) => {
      control.addEventListener(
        'click',
        () => {
          this.setOpen(true);
          // An empty attribute just opens the sheet at the top.
          const selector = control.getAttribute('data-drop-open-details');
          const target = selector ? this.querySelector(selector) : null;
          if (target instanceof HTMLElement) {
            // After the growth animation, or the offset is measured mid-flight.
            setTimeout(() => {
              this.scrollTop = Math.max(0, target.offsetTop - this.offsetTop - 16);
            }, SHEET_CLOSE_MS);
          }
        },
        { signal }
      );
    });
    this.closeButton?.addEventListener('click', () => this.setOpen(false), { signal });
    this.scrim?.addEventListener('click', () => this.setOpen(false), { signal });

    this.setOpen(false, { initial: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.onKeydown);
    this.lockScroll(false);
  }

  lockScroll(locked) {
    const scroller = this.closest('.drop-story__scroller');
    const target = scroller instanceof HTMLElement ? scroller : document.documentElement;
    target.style.overflow = locked ? 'hidden' : '';
  }

  setOpen(open, { initial = false } = {}) {
    // Everything is already on screen in the split layout.
    if (open && this.splitLayout?.matches) return;
    if (open === this.open && !initial) return;
    this.open = open;
    this.handle.setAttribute('aria-expanded', String(open));

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (open) {
      clearTimeout(this.closeTimer);
      // Measured while still in flow: this is the height to grow from, and to
      // shrink back to on close.
      this.collapsedHeight = this.offsetHeight;
      // The sheet is fixed, so it leaves the flow and the media would grow to
      // fill the chapter — then snap back when the sheet returns. Holding the
      // panel's space keeps the image exactly where it was throughout.
      // The panel overlaps the media by its negative margin-top, so the space
      // it actually occupies in the flow is that much less than its height.
      const overlap = parseFloat(getComputedStyle(this).marginTop) || 0;
      this.chapter?.style.setProperty('--drop-panel-reserved', `${this.collapsedHeight + overlap}px`);
      this.chapter?.classList.add('is-sheet-open');
      this.classList.remove('is-closing');
      this.classList.add('is-open');
      if (this.scrim) this.scrim.hidden = false;
      this.lockScroll(true);

      if (!reduced) {
        this.style.height = `${this.collapsedHeight}px`;
        void this.offsetHeight; // reflow, so the change animates
        this.style.height = ''; // transition up to the height in CSS
      }

      this.lastFocus = document.activeElement;
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      document.addEventListener('keydown', this.onKeydown);
      this.scrollTop = 0;
      this.closeButton?.focus();

      const chapter = this.chapter;
      if (chapter?.dataset.productId && document.querySelector('.drop-story[data-drop-analytics]')) {
        track('drop_details_opened', {
          productId: chapter.dataset.productId,
          handle: chapter.dataset.productHandle,
          title: chapter.dataset.productTitle,
          path: window.location.pathname,
        });
      }
      return;
    }

    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    document.removeEventListener('keydown', this.onKeydown);

    // The sheet stays positioned while it shrinks, and the open state is only
    // dropped once it matches the collapsed panel. Sliding it off-screen first
    // meant it vanished and then reappeared in place.
    const settle = () => {
      this.classList.remove('is-open', 'is-closing');
      this.scrim?.classList.remove('is-closing');
      // Released in the same frame the panel rejoins the flow, so the media
      // never sees an intermediate size.
      this.chapter?.classList.remove('is-sheet-open');
      this.chapter?.style.removeProperty('--drop-panel-reserved');
      this.style.height = '';
      if (this.scrim) this.scrim.hidden = true;
      this.lockScroll(false);
    };

    const animating = !initial && this.classList.contains('is-open') && !reduced;

    if (animating) {
      this.classList.add('is-closing');
      this.scrim?.classList.add('is-closing');
      this.style.height = `${this.offsetHeight}px`;
      void this.offsetHeight; // reflow
      this.style.height = `${this.collapsedHeight ?? 0}px`;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(settle, SHEET_CLOSE_MS);
    } else {
      settle();
    }

    // Send focus back to what opened the sheet, but not on first render.
    if (!initial && this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
  }
}

/**
 * Progressive buy flow.
 *
 * Sizes stay closed until "Select a size" is pressed; picking one swaps that
 * button for the real Add to cart. Horizon's buy-buttons block is only shown
 * or hidden, never replaced, so /cart/add.js and the product_added_to_cart
 * event are untouched.
 *
 * Chosen state lives in a module-level Set rather than on the element, because
 * selecting a variant makes Horizon re-render the picker — which would
 * otherwise reset a freshly constructed element straight back to "unchosen".
 */
const chosenSizes = new Map();

class DropBuy extends HTMLElement {
  connectedCallback() {
    this.choose = this.querySelector('[data-drop-choose]');
    this.change = this.querySelector('[data-drop-change]');
    this.chosenValue = this.querySelector('[data-drop-chosen-size]');
    this.productId = this.dataset.productId || '';

    // A product with no size options has nothing to choose.
    if (!this.choose) {
      this.classList.add('is-chosen');
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    // Both controls toggle, so an opened drawer can always be closed again.
    this.choose.addEventListener('click', () => this.setSizesOpen(!this.sizesOpen), { signal });
    this.change?.addEventListener('click', () => this.setSizesOpen(!this.sizesOpen), { signal });

    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      const label = event.target.closest('label');
      const text = label?.querySelector('.variant-option__button-label__text') ?? label;
      this.setChosen(text?.textContent.trim() ?? '');
      this.setSizesOpen(false);
    }, { signal });

    if (chosenSizes.has(this.productId)) this.setChosen(chosenSizes.get(this.productId));
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  get sizesOpen() {
    return this.classList.contains('is-sizes-open');
  }

  setSizesOpen(open) {
    this.classList.toggle('is-sizes-open', open);
    this.choose.setAttribute('aria-expanded', String(open));
    this.change?.setAttribute('aria-expanded', String(open));
  }

  setChosen(label) {
    chosenSizes.set(this.productId, label);
    this.classList.add('is-chosen');
    if (this.chosenValue) this.chosenValue.textContent = label;
  }
}

/**
 * Keeps the story's viewport height in step with the header group.
 *
 * critical.js publishes --header-group-height once at load, so anything that
 * changes the header afterwards — dismissing an announcement bar, a sticky
 * header collapsing — leaves full-height sections mis-sized until a refresh.
 * Watching it directly means the story is correct whatever the cause.
 */
/**
 * Mirrors calculateHeaderGroupHeight in critical.js: sum the header group's
 * children, skipping the header component itself, and add it back only for a
 * transparent header. Replicated rather than imported so a module-resolution
 * failure can never take the rest of this file down with it — but it must stay
 * in step with the theme, because --header-group-height is read by other
 * sections and a different number here would mis-size them.
 */
function headerGroupHeight() {
  const group = document.getElementById('header-group');
  if (!group) return 0;

  const header = document.querySelector('header-component');
  let total = 0;

  for (const child of group.children) {
    if (child === header || !(child instanceof HTMLElement)) continue;
    total += child.offsetHeight;
  }

  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return total + header.offsetHeight;
  }

  return total;
}

function syncStoryViewport() {
  const stories = document.querySelectorAll('.drop-story');
  if (!stories.length) return;

  const height = Math.round(headerGroupHeight());
  stories.forEach((story) => story.style.setProperty('--drop-header-height', `${height}px`));

  // header.js observes the group but discards the result without writing the
  // variable, so nothing else updates it either. Publish it for the theme.
  document.body.style.setProperty('--header-group-height', `${height}px`);
}

// Deliberately NOT run on load. Horizon declares --header-group-height in CSS,
// which is already right, whereas measuring here happens before the logo image
// has loaded — a short header, and chapters too tall to fit. Only override once
// the header genuinely changes size.
document.addEventListener('drop:header-resized', syncStoryViewport);

if ('ResizeObserver' in window) {
  const group = document.getElementById('header-group');

  if (group) {
    let initialObservation = true;

    new ResizeObserver(() => {
      // The first callback is just the starting size, not a change.
      if (initialObservation) {
        initialObservation = false;
        return;
      }
      syncStoryViewport();
    }).observe(group);
  }
}

/**
 * Analytics.
 *
 * These are CUSTOM events, not Shopify standard ones — a theme cannot emit
 * standard events, so this does not restore product_viewed / ViewContent for
 * the native Meta and Google channels. It gives a custom pixel (Settings >
 * Customer events) something to subscribe to, and dispatches the same payload
 * as a DOM event so GTM or an app embed can listen without a pixel at all.
 *
 * Nothing here may ever break the page, hence the try/catch and the guards.
 */
const trackedChapters = new Set();

function track(name, payload) {
  document.dispatchEvent(new CustomEvent(`drop-story:${name}`, { detail: payload }));

  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    // An analytics failure is never worth taking the page down for.
  }
}

function chapterPayload(chapter, index, total) {
  const price = Number(chapter.dataset.productPrice);

  return {
    productId: chapter.dataset.productId,
    variantId: chapter.dataset.variantId,
    handle: chapter.dataset.productHandle,
    title: chapter.dataset.productTitle,
    // Shopify prices are in cents.
    price: Number.isFinite(price) ? price / 100 : null,
    currency: window.Shopify?.currency?.active ?? null,
    position: index + 1,
    total,
    path: window.location.pathname,
  };
}

function watchStory() {
  const story = document.querySelector('.drop-story[data-drop-analytics]');
  if (!story || !('IntersectionObserver' in window)) return;

  const chapters = Array.from(story.querySelectorAll('.drop-chapter[data-product-id]'));
  const endCard = story.querySelector('.drop-story__end');

  // Mostly-visible, so a chapter scrolled past at speed is not counted.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        if (entry.target === endCard) {
          observer.unobserve(endCard);
          track('drop_story_completed', { chapters: chapters.length, path: window.location.pathname });
          return;
        }

        const index = chapters.indexOf(entry.target);
        const id = entry.target.dataset.productId;
        // Once per page view, so scrolling back and forth does not inflate counts.
        if (index < 0 || trackedChapters.has(id)) return;

        trackedChapters.add(id);
        track('drop_chapter_viewed', chapterPayload(entry.target, index, chapters.length));
      });
    },
    { threshold: 0.6 }
  );

  chapters.forEach((chapter) => observer.observe(chapter));
  if (endCard) observer.observe(endCard);

  // Do not depend on the observer's first callback arriving before the visitor
  // starts scrolling — check what is already on screen straight away. Both
  // paths funnel through trackedChapters, so nothing is counted twice.
  sweepVisibleChapters(chapters);
}

function sweepVisibleChapters(chapters) {
  const viewport = window.innerHeight || document.documentElement.clientHeight;

  chapters.forEach((chapter, index) => {
    const id = chapter.dataset.productId;
    if (!id || trackedChapters.has(id)) return;

    const rect = chapter.getBoundingClientRect();
    if (!rect.height) return;

    const visible = Math.min(rect.bottom, viewport) - Math.max(rect.top, 0);
    if (visible / rect.height < 0.6) return;

    trackedChapters.add(id);
    track('drop_chapter_viewed', chapterPayload(chapter, index, chapters.length));
  });
}

watchStory();
document.addEventListener('shopify:section:load', watchStory);

/**
 * Chapter counters ("Drop 1/2").
 *
 * A chapter has no idea of its own position: blocks render through
 * content_for 'blocks' with no loop index, and the total is only known once
 * they are all in the DOM. So they are numbered here, and renumbered on the
 * theme editor's load events when chapters are added or reordered.
 */
function labelChapters() {
  document.querySelectorAll('.drop-story__scroller').forEach((scroller) => {
    const chapters = scroller.querySelectorAll('.drop-chapter');

    chapters.forEach((chapter, index) => {
      const counter = chapter.querySelector('[data-drop-counter]');
      if (!counter) return;

      // Nothing to reassure anyone about with a single chapter.
      if (chapters.length < 2) {
        counter.hidden = true;
        return;
      }

      const label = counter.getAttribute('data-drop-counter') || '';
      counter.textContent = `${label} ${index + 1}/${chapters.length}`.trim();
      counter.hidden = false;
    });
  });
}

labelChapters();
document.addEventListener('shopify:section:load', labelChapters);
document.addEventListener('shopify:block:load', labelChapters);
document.addEventListener('shopify:block:deselect', labelChapters);

/**
 * Scroll cues. Delegated once for the page, so cues inside chapters rendered
 * later by the theme editor work without rebinding.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element && event.target.closest('[data-drop-scroll-next]');
  if (!trigger) return;

  const section = trigger.closest('.drop-story__scroller > *');
  const next = section?.nextElementSibling;
  if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/**
 * Waitlist button on a coming-soon chapter. Jumps to the signup the story
 * already ends on, rather than the chapter carrying a second form of its own.
 * Delegated for the same reason as the cues above: chapters the theme editor
 * renders later work without rebinding.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element && event.target.closest('[data-drop-scroll-end]');
  if (!trigger) return;

  const end = trigger.closest('.drop-story')?.querySelector('.drop-story__end');
  if (!end) return;

  end.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

if (!customElements.get('drop-zoom')) {
  customElements.define('drop-zoom', DropZoom);
}

if (!customElements.get('drop-buy')) {
  customElements.define('drop-buy', DropBuy);
}

if (!customElements.get('drop-panel')) {
  customElements.define('drop-panel', DropPanel);
}

if (!customElements.get('drop-gallery')) {
  customElements.define('drop-gallery', DropGallery);
}
